/**
 * useAnalyticsSettings Hook
 *
 * React hook for managing project-level analytics settings.
 * Provides real-time subscription and CRUD operations.
 *
 * @module analytics-settings/hooks
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type {
  ProjectAnalyticsSettings,
  AnalyticsIntegration,
  ConsentManagementConfig,
  AnalyticsPlatform,
  PlatformConfig,
} from '../types/analytics.types';
import { DEFAULT_ANALYTICS_SETTINGS, getDefaultConfig } from '../types/analytics.types';
import { getProjectAnalyticsService } from '../services/ProjectAnalyticsService';

export interface UseAnalyticsSettingsResult {
  /** Current analytics settings */
  settings: ProjectAnalyticsSettings;
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: Error | null;
  /** Saving state */
  saving: boolean;

  // Consent Management
  /** Update consent management configuration */
  updateConsentManagement: (config: ConsentManagementConfig) => Promise<void>;

  // Integration CRUD
  /** Add a new integration */
  addIntegration: (
    platform: AnalyticsPlatform,
    name: string,
    config: PlatformConfig
  ) => Promise<AnalyticsIntegration>;
  /** Update an existing integration */
  updateIntegration: (
    integrationId: string,
    updates: Partial<Omit<AnalyticsIntegration, 'id' | 'createdAt'>>
  ) => Promise<void>;
  /** Delete an integration */
  deleteIntegration: (integrationId: string) => Promise<void>;
  /** Toggle integration enabled/disabled */
  toggleIntegration: (integrationId: string, enabled: boolean) => Promise<void>;

  // Utilities
  /** Refresh settings from Firestore */
  refresh: () => Promise<void>;
  /** Get enabled integrations only */
  enabledIntegrations: AnalyticsIntegration[];
}

/**
 * Hook for managing project analytics settings
 *
 * @param projectId - The project ID to manage settings for
 * @returns Analytics settings state and operations
 *
 * @example
 * ```tsx
 * function AnalyticsPage() {
 *   const { settings, loading, addIntegration, toggleIntegration } = useAnalyticsSettings(projectId);
 *
 *   if (loading) return <LoadingSpinner />;
 *
 *   return (
 *     <div>
 *       {settings.integrations.map(integration => (
 *         <IntegrationCard
 *           key={integration.id}
 *           integration={integration}
 *           onToggle={() => toggleIntegration(integration.id, !integration.enabled)}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAnalyticsSettings(projectId: string | undefined): UseAnalyticsSettingsResult {
  const [settings, setSettings] = useState<ProjectAnalyticsSettings>(DEFAULT_ANALYTICS_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);

  const service = useMemo(() => getProjectAnalyticsService(), []);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!projectId) {
      setSettings(DEFAULT_ANALYTICS_SETTINGS);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = service.subscribeToSettings(projectId, (newSettings) => {
      setSettings(newSettings);
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [projectId, service]);

  // Refresh settings manually
  const refresh = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const newSettings = await service.getSettings(projectId);
      setSettings(newSettings);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load settings'));
    } finally {
      setLoading(false);
    }
  }, [projectId, service]);

  // Update consent management
  const updateConsentManagement = useCallback(
    async (config: ConsentManagementConfig) => {
      if (!projectId) throw new Error('No project ID');

      setSaving(true);
      try {
        await service.updateConsentManagement(projectId, config);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to update consent settings'));
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [projectId, service]
  );

  // Add integration
  const addIntegration = useCallback(
    async (
      platform: AnalyticsPlatform,
      name: string,
      config: PlatformConfig
    ): Promise<AnalyticsIntegration> => {
      if (!projectId) throw new Error('No project ID');

      setSaving(true);
      try {
        const now = new Date().toISOString();
        const integration = await service.addIntegration(projectId, {
          platform,
          name,
          enabled: true,
          config: config ?? getDefaultConfig(platform),
          createdAt: now,
          updatedAt: now,
        });
        setError(null);
        return integration;
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to add integration'));
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [projectId, service]
  );

  // Update integration
  const updateIntegration = useCallback(
    async (
      integrationId: string,
      updates: Partial<Omit<AnalyticsIntegration, 'id' | 'createdAt'>>
    ) => {
      if (!projectId) throw new Error('No project ID');

      setSaving(true);
      try {
        await service.updateIntegration(projectId, integrationId, updates);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to update integration'));
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [projectId, service]
  );

  // Delete integration
  const deleteIntegration = useCallback(
    async (integrationId: string) => {
      if (!projectId) throw new Error('No project ID');

      setSaving(true);
      try {
        await service.deleteIntegration(projectId, integrationId);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to delete integration'));
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [projectId, service]
  );

  // Toggle integration
  const toggleIntegration = useCallback(
    async (integrationId: string, enabled: boolean) => {
      if (!projectId) throw new Error('No project ID');

      setSaving(true);
      try {
        await service.toggleIntegration(projectId, integrationId, enabled);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to toggle integration'));
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [projectId, service]
  );

  // Enabled integrations
  const enabledIntegrations = useMemo(
    () => settings.integrations.filter((i) => i.enabled),
    [settings.integrations]
  );

  return {
    settings,
    loading,
    error,
    saving,
    updateConsentManagement,
    addIntegration,
    updateIntegration,
    deleteIntegration,
    toggleIntegration,
    refresh,
    enabledIntegrations,
  };
}
